import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  UserPlus,
  ClipboardList,
  Users as UsersIcon,
  Building2,
  MessageSquare,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/register", label: "New Registration", icon: UserPlus },
  { to: "/registrations", label: "Registrations", icon: ClipboardList },
  { to: "/sms", label: "SMS", icon: MessageSquare, adminOnly: true },
  { to: "/users", label: "User Management", icon: UsersIcon, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 flex flex-col bg-slate-900 text-slate-100 transform transition-transform duration-300 md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-800">
          <div className="h-9 w-9 rounded-lg bg-blue-800 flex items-center justify-center">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading font-bold text-sm leading-tight">Haryana Safai Mahakumbh</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Admin Console</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems
            .filter((i) => !i.adminOnly || user?.role === "admin")
            .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-blue-800 flex items-center justify-center font-heading font-bold text-sm uppercase">
              {user?.username?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.username}</p>
              <Badge
                variant="secondary"
                className="mt-0.5 text-[10px] uppercase bg-slate-800 text-slate-300 border-0"
              >
                {user?.role}
              </Badge>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 h-9"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-20 flex items-center justify-between bg-slate-900 text-white px-4 h-14">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setOpen((v) => !v)}
              variant="ghost"
              size="sm"
              className="text-slate-100 hover:bg-slate-800 hover:text-white px-2"
              data-testid="sidebar-toggle-button"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-heading font-bold">Haryana Safai Mahakumbh</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-slate-300 hover:bg-slate-800 hover:text-white">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
